import type {
  SurfacePrintEffectKind,
  SurfacePrintEffectSetting,
  SurfacePrintEffects,
} from './types';

export const SURFACE_PRINT_EFFECT_KINDS = [
  'distress',
  'washed',
  'grain',
  'cracks',
] as const satisfies readonly SurfacePrintEffectKind[];

export const SURFACE_PRINT_EFFECT_ALGORITHM_VERSION = 1 as const;
export const MAX_SURFACE_PRINT_EFFECT_SEED = 0xffff_ffff;

const DEFAULTS: Readonly<SurfacePrintEffects> = Object.freeze({
  algorithmVersion: SURFACE_PRINT_EFFECT_ALGORITHM_VERSION,
  distress: Object.freeze({ intensity: 0, seed: 0x6d2b_79f5 }),
  washed: Object.freeze({ intensity: 0, seed: 0xa511_e9b3 }),
  grain: Object.freeze({ intensity: 0, seed: 0x9e37_79b9 }),
  cracks: Object.freeze({ intensity: 0, seed: 0x85eb_ca6b }),
});

export const DEFAULT_SURFACE_PRINT_EFFECTS: Readonly<SurfacePrintEffects> = DEFAULTS;

export function createDefaultSurfacePrintEffects(): SurfacePrintEffects {
  return {
    algorithmVersion: SURFACE_PRINT_EFFECT_ALGORITHM_VERSION,
    distress: { ...DEFAULTS.distress },
    washed: { ...DEFAULTS.washed },
    grain: { ...DEFAULTS.grain },
    cracks: { ...DEFAULTS.cracks },
  };
}

export function createSurfacePrintEffectSeed(): number {
  if (typeof globalThis.crypto?.getRandomValues === 'function') {
    const value = new Uint32Array(1);
    globalThis.crypto.getRandomValues(value);
    return value[0]!;
  }
  return Math.floor(Math.random() * (MAX_SURFACE_PRINT_EFFECT_SEED + 1));
}

/** Concise UI-facing alias for randomising a procedural effect. */
export const createPrintEffectSeed = createSurfacePrintEffectSeed;

export function resolveSurfacePrintEffects(
  effects?: Partial<SurfacePrintEffects> | null,
): SurfacePrintEffects {
  return {
    algorithmVersion: SURFACE_PRINT_EFFECT_ALGORITHM_VERSION,
    distress: resolveSetting(effects?.distress, DEFAULTS.distress),
    washed: resolveSetting(effects?.washed, DEFAULTS.washed),
    grain: resolveSetting(effects?.grain, DEFAULTS.grain),
    cracks: resolveSetting(effects?.cracks, DEFAULTS.cracks),
  };
}

export function isSurfacePrintEffectActive(setting?: SurfacePrintEffectSetting): boolean {
  return Boolean(setting && setting.intensity > 0);
}

export function getActiveSurfacePrintEffects(
  effects?: SurfacePrintEffects,
): Array<readonly [SurfacePrintEffectKind, SurfacePrintEffectSetting]> {
  const resolved = resolveSurfacePrintEffects(effects);
  return SURFACE_PRINT_EFFECT_KINDS.filter(kind => isSurfacePrintEffectActive(resolved[kind])).map(
    kind => [kind, resolved[kind]] as const,
  );
}

export function hasActivePrintEffects(effects?: SurfacePrintEffects): boolean {
  return getActiveSurfacePrintEffects(effects).length > 0;
}

function resolveSetting(
  setting: Partial<SurfacePrintEffectSetting> | undefined,
  fallback: Readonly<SurfacePrintEffectSetting>,
): SurfacePrintEffectSetting {
  return {
    intensity: clampUnit(setting?.intensity ?? fallback.intensity),
    seed: normalizeSeed(setting?.seed, fallback.seed),
  };
}

function clampUnit(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(1, Math.max(0, value));
}

function normalizeSeed(value: number | undefined, fallback: number): number {
  if (
    !Number.isInteger(value) ||
    value === undefined ||
    value < 0 ||
    value > MAX_SURFACE_PRINT_EFFECT_SEED
  ) {
    return fallback;
  }
  return value;
}
